import axios from 'axios';
import toast from 'react-hot-toast';

// ─── AXIOS INSTANCE ──────────────────────────────────────────
const http = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

http.interceptors.request.use(cfg => {
  // Token read from Zustand store (avoids localStorage)
  try {
    const { useAuthStore } = require('../store/useAuthStore');
    const token = useAuthStore.getState().token;
    if (token) cfg.headers.Authorization = `Bearer ${token}`;
  } catch {}
  return cfg;
});

http.interceptors.response.use(
  res => res,
  err => {
    const msg = err.response?.data?.detail || err.response?.data?.message || 'Erro inesperado';
    if (err.response?.status === 401) {
      try {
        const { useAuthStore } = require('../store/useAuthStore');
        useAuthStore.getState().logout();
      } catch {}
    } else if (err.response?.status >= 500) {
      toast.error(msg);
    }
    return Promise.reject(err);
  }
);

// ─── AUTH API ─────────────────────────────────────────────────
export const authService = {
  login: (login, password) =>
    http.post('/auth/login', { login, password }).then(r => r.data),
};

// ─── CONTACTS API ─────────────────────────────────────────────
export const contactsApi = {
  search: (q) => http.get('/contacts/search', { params: { q } }).then(r => r.data),
  searchCnpj: (cnpj) => http.get(`/contacts/search-cnpj/${cnpj}`).then(r => r.data),
  getById: (id) => http.get(`/contacts/${id}`).then(r => r.data),

};

// ─── CASES API ────────────────────────────────────────────────
export const casesApi = {
  list: (params = {}) => http.get('/cases', { params }).then(r => r.data),
  getById: (id) => http.get(`/cases/${id}`).then(r => r.data),
  create: (data) => http.post('/cases', data).then(r => r.data),
  updateStatus: (id, status, caseNumber) => {
    const body = caseNumber ? { status, case_number: caseNumber } : { status };
    return http.patch(`/cases/${id}/status`, body).then(r => r.data);
  },
  getPreGenerationCheck: (id) => http.get(`/cases/${id}/pre-generation-check`).then(r => r.data),
  getPerguntas: (id) => http.get(`/cases/${id}/perguntas`).then(r => r.data),
  responderPerguntas: (id, respostas) =>
    http.post(`/cases/${id}/perguntas`, { respostas }).then(r => r.data),
  // O id da pendência traz ":" (`pendencia:diferencas_rescisorias`): vai codificado no caminho.
  dispensarPendencia: (id, pendenciaId, justificativa) =>
    http.post(`/cases/${id}/pendencias/${encodeURIComponent(pendenciaId)}/dispensar`, { justificativa })
      .then(r => r.data),
  reabrirPendencia: (id, pendenciaId) =>
    http.post(`/cases/${id}/pendencias/${encodeURIComponent(pendenciaId)}/reabrir`).then(r => r.data),
  getGenerationStatus: (id) => http.get(`/cases/${id}/generation-status`).then(r => r.data),
  archive: (id) => http.post(`/cases/${id}/archive`).then(r => r.data),
  unarchive: (id) => http.post(`/cases/${id}/unarchive`).then(r => r.data),
  addToCorpus: (id) => http.post(`/cases/${id}/add-to-corpus`).then(r => r.data),
  remove: (id) => http.delete(`/cases/${id}`).then(r => r.data),
};

// ─── FORMS API ────────────────────────────────────────────────
export const formsApi = {
  getByCaseId: (caseId) => http.get(`/intake-forms/${caseId}`).then(r => r.data),
  save: (caseId, data) => http.put(`/intake-forms/${caseId}`, data).then(r => r.data),
  createWithCase: (contactId, formData) =>
    http.post('/cases', { contact_id: contactId, form_data: formData }).then(r => r.data),
  aiAudit: (caseId) => http.post(`/intake-forms/${caseId}/ai-audit`).then(r => r.data),
  // Propõe estruturar o que só existe na prosa das observações. Não grava: devolve a proposta
  // já conferida contra o texto da ficha, e o que não estiver escrito lá vem em `recusas`.
  estruturarObservacoes: (caseId) =>
    http.post(`/intake-forms/${caseId}/estruturar-observacoes`).then(r => r.data),
  // Modelo oficial de ficha em DOCX: imprimir/preencher no Word e reimportar por
  // `importDocument`, que lê as tags direto do arquivo (sem custo de IA).
  downloadModeloFicha: () =>
    http.get('/intake-forms/modelo-ficha', { responseType: 'blob' }).then(r => r.data),
  importDocument: (caseId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return http.post(`/intake-forms/${caseId}/import-document`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },
  // Petição-modelo: peça já pronta que o escritório indica como espelho para este caso.
  getPeticaoModelo: (caseId) => http.get(`/intake-forms/${caseId}/peticao-modelo`).then(r => r.data),
  uploadPeticaoModelo: (caseId, file, preencherFicha = false) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('preencher_ficha', String(preencherFicha));
    return http.post(`/intake-forms/${caseId}/peticao-modelo`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },
  deletePeticaoModelo: (caseId) => http.delete(`/intake-forms/${caseId}/peticao-modelo`).then(r => r.data),
  getChatHistory: (caseId) => http.get(`/intake-forms/${caseId}/chat`).then(r => r.data),
  sendChatMessage: (caseId, content) => http.post(`/intake-forms/${caseId}/chat`, { content }).then(r => r.data),
  getClaimantSuggestions: (cpf, excludeCaseId = '') =>
    http.get('/intake-forms/claimant-suggestions', { params: { cpf, exclude_case_id: excludeCaseId } }).then(r => r.data),
  acceptClaimantPrefill: (sourceCaseId, targetCaseId, fields) =>
    http.post('/intake-forms/claimant-prefill-accept', {
      source_case_id: sourceCaseId,
      target_case_id: targetCaseId,
      fields
    }).then(r => r.data),
};

export const addressesApi = {
  cities: (uf) =>
    http.get('/addresses/cities', { params: { uf } }).then(r => r.data),
  search: (uf, city, q) =>
    http.get('/addresses/search', { params: { uf, city, q } }).then(r => r.data),
};

export const customFieldsApi = {
  list: (includeInactive = false) =>
    http.get('/settings/fields', { params: includeInactive ? { include_inactive: true } : {} }).then(r => r.data),
  create: (data) => http.post('/settings/fields', data).then(r => r.data),
  update: (id, data) => http.patch(`/settings/fields/${id}`, data).then(r => r.data),
  remove: (id) => http.delete(`/settings/fields/${id}`).then(r => r.data),
};

// ─── PETITIONS API ────────────────────────────────────────────
export const petitionsApi = {
  // A geração real (classificador + ~10 seções via LLM) leva 1–3 min; o timeout global de
  // 30s derrubaria uma geração bem-sucedida.
  generate: (caseId) =>
    http.post('/petitions/generate', { case_id: caseId }, { timeout: 300000 }).then(r => r.data),

  getByCaseId: (caseId) => http.get(`/petitions/by-case/${caseId}`).then(r => r.data),

  uploadRevision: (petitionId, file, reviewerNotes) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('reviewer_notes', reviewerNotes);
    return http.post(`/petitions/${petitionId}/upload-revision`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },

  submitApproval: (petitionId) => http.post(`/petitions/${petitionId}/submit-approval`).then(r => r.data),
  // `motivo` (opcional, 1.23) fica gravado na peça como conferência dos críticos assumidos.
  enviarParaRevisao: (petitionId, motivo) =>
    http.post(`/petitions/${petitionId}/enviar-para-revisao`, motivo ? { motivo } : undefined).then(r => r.data),
  saveReviewNote: (petitionId, reviewerNotes) => http.post(`/petitions/${petitionId}/review-notes`, { reviewer_notes: reviewerNotes }).then(r => r.data),
  retryLearning: (petitionId, revisionId) => http.post(`/petitions/${petitionId}/revisions/${revisionId}/retry-learning`).then(r => r.data),
  getRevision: (petitionId) => http.get(`/petitions/${petitionId}/revision`).then(r => r.data),
  regenerateFromRevision: (petitionId, payload = {}) =>
    http.post(`/petitions/${petitionId}/regenerate-from-revision`, payload).then(r => r.data),
  approve: (petitionId) => http.post(`/petitions/${petitionId}/approve`).then(r => r.data),
  reject: (petitionId, reason) => http.post(`/petitions/${petitionId}/reject`, { reason }).then(r => r.data),

  download: (petitionId, format = 'docx') =>
    http.get(`/petitions/${petitionId}/download`, { params: { format }, responseType: 'blob' }).then(r => r.data),

  getVersions: (caseId) => http.get(`/petitions/by-case/${caseId}/versions`).then(r => r.data),
  getCalculo: (caseId) => http.get(`/petitions/by-case/${caseId}/calculo`).then(r => r.data),

  getLearnings: (params) => http.get('/petitions/curation/learnings', { params }).then(r => r.data),
  acceptLearning: (id, payload) => http.put(`/petitions/curation/learnings/${id}/accept`, payload).then(r => r.data),
  rejectLearning: (id) => http.put(`/petitions/curation/learnings/${id}/reject`).then(r => r.data),
  deactivateLearning: (id) => http.put(`/petitions/curation/learnings/${id}/deactivate`).then(r => r.data),
};

// ─── METRICS API ──────────────────────────────────────────────
export const metricsApi = {
  get: () => http.get('/metrics').then(r => r.data),
  getLlmCosts: (params = {}) => http.get('/metrics/llm-costs', { params }).then(r => r.data),
  getLlmUsage: (params = {}) => http.get('/metrics/llm-usage', { params }).then(r => r.data),
  getLlmUsageBudgetStatus: () => http.get('/metrics/llm-usage/budget-status').then(r => r.data),
};

// ─── USERS API ────────────────────────────────────────────────
export const usersApi = {
  list: () => http.get('/users').then(r => r.data),
  listLawyers: () => http.get('/users/lawyers').then(r => r.data),
  create: (data) => http.post('/users', data).then(r => r.data),
  update: (id, data) => http.patch(`/users/${id}`, data).then(r => r.data),
  updateMe: (data) => http.patch('/users/me', data).catch(err => {
    if (err.response?.status === 404) {
      const { useAuthStore } = require('../store/useAuthStore');
      const currentUser = useAuthStore.getState().user;
      if (currentUser?.id) return http.patch(`/users/${currentUser.id}`, data);
    }
    return Promise.reject(err);
  }).then(r => r.data),
  getMe: () => http.get('/users/me').catch(err => {
    if (err.response?.status === 404) {
      const { useAuthStore } = require('../store/useAuthStore');
      const currentUser = useAuthStore.getState().user;
      if (currentUser) return { data: currentUser };
    }
    return Promise.reject(err);
  }).then(r => r.data),
  uploadAvatar: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return http.post(`/users/${id}/avatar`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
  getAvatarBlob: (id) => http.get(`/users/${id}/avatar`, { responseType: 'blob' }).then(r => r.data),
};

export const rolesApi = {
  list: () => http.get('/roles').then(r => r.data),
  create: (data) => http.post('/roles', data).then(r => r.data),
  update: (id, data) => http.patch(`/roles/${id}`, data).then(r => r.data),
  remove: (id) => http.delete(`/roles/${id}`).then(r => r.data),
  applyToUsers: (id) => http.post(`/roles/${id}/apply-to-users`).then(r => r.data),
  // Perfis que aparecem no crachá dos usuários e não existem no cadastro.
  listEmUso: () => http.get('/roles/em-uso').then(r => r.data),
  registrarEmUso: (data) => http.post('/roles/em-uso/registrar', data).then(r => r.data),
};

// ─── JURISPRUDENCIAS API ──────────────────────────────────────
export const textosPadraoApi = {
  list: () => http.get('/textos-padrao').then(r => r.data),
  get: (chave, params) => http.get(`/textos-padrao/${chave}`, { params }).then(r => r.data),
  publicar: (chave, data) => http.post(`/textos-padrao/${chave}/publicar`, data).then(r => r.data),
  reverter: (chave, data) => http.post(`/textos-padrao/${chave}/reverter`, data).then(r => r.data),
};

export const jurisprudenciasApi = {
  list: (includeInactive = false) =>
    http.get('/jurisprudencias', { params: includeInactive ? { include_inactive: true } : {} })
      .then(r => r.data),
  create: (data) => http.post('/jurisprudencias', data).then(r => r.data),
  update: (id, data) => http.patch(`/jurisprudencias/${id}`, data).then(r => r.data),
  remove: (id) => http.delete(`/jurisprudencias/${id}`).then(r => r.data),
};

// ─── SETTINGS (LLM) API ────────────────────────────────────────
export const settingsApi = {
  getLlm: () => http.get('/settings/llm').then(r => r.data),
  getLlmCredits: () => http.get('/settings/llm/credits').then(r => r.data),
  updateLlm: (data) => http.put('/settings/llm', data).then(r => r.data),
  testLlm: (data = {}) => http.post('/settings/llm/test', data).then(r => r.data),
  getOffice: () => http.get('/settings/office').then(r => r.data),
  updateOffice: (data) => http.put('/settings/office', data).then(r => r.data),
  updateParametros: (valores) => http.put('/settings/parametros', { valores }).then(r => r.data),
  listTemplates: (includeInactive = false) =>
    http.get('/settings/templates', { params: includeInactive ? { include_inactive: true } : {} }).then(r => r.data),
  createTemplate: (data) => http.post('/settings/templates', data).then(r => r.data),
  updateTemplate: (id, data) => http.patch(`/settings/templates/${id}`, data).then(r => r.data),
  removeTemplate: (id) => http.delete(`/settings/templates/${id}`).then(r => r.data),
  listIntegrations: () => http.get('/settings/integrations').then(r => r.data),
  createIntegration: (data) => http.post('/settings/integrations', data).then(r => r.data),
  updateIntegration: (id, data) => http.patch(`/settings/integrations/${id}`, data).then(r => r.data),
  removeIntegration: (id) => http.delete(`/settings/integrations/${id}`).then(r => r.data),

};

export default http;
